<template>
  <div class="ledger-outstanding-upload-container">
    <b-card class="upload-card">
      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="text-center">
          <b-spinner variant="primary" label="Loading"></b-spinner>
          <p class="mt-2 mb-0">{{ loadingMessage }}</p>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <b-alert
        v-if="successMessage"
        variant="success"
        dismissible
        @dismissed="successMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-check-circle mr-2"></i>
        {{ successMessage }}
      </b-alert>

      <b-alert
        v-if="errorMessage"
        variant="danger"
        dismissible
        @dismissed="errorMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMessage }}
      </b-alert>

      <!-- File Upload Section -->
      <b-row>
        <b-col md="12" lg="6">
          <div class="file-upload-section">
            <label class="upload-label">
              <i class="fa fa-file-excel-o mr-2" style="color: #28a745"></i>
              Select Excel File
            </label>
            <b-form-file
              v-model="selectedFile"
              :state="fileState"
              accept=".xlsx,.xls"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @input="handleFile"
              class="mb-2"
              :disabled="isLoading"
            ></b-form-file>
            <small class="form-text text-muted">
              <i class="fa fa-info-circle mr-1"></i>
              Accepted formats: .xlsx, .xls (Excel files only)
            </small>
          </div>
        </b-col>
        <b-col md="12" lg="6">
          <div v-if="selectedFile" class="file-info-section">
            <b-card bg-variant="light" class="mb-0">
              <div class="d-flex align-items-center">
                <i
                  class="fa fa-file-excel-o mr-2"
                  style="font-size: 24px; color: #28a745"
                ></i>
                <div class="flex-grow-1">
                  <strong>{{ selectedFile.name }}</strong>
                  <br />
                  <small class="text-muted">{{
                    formatFileSize(selectedFile.size)
                  }}</small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearFile"
                  :disabled="isLoading"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </b-card>
          </div>
        </b-col>
      </b-row>

      <!-- Data Preview Section -->
      <div v-if="jsonData.length > 0" class="preview-section mt-4">
        <b-card>
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <i class="fa fa-table mr-2"></i>
                <strong>Data Preview</strong>
                <b-badge variant="info" class="ml-2"
                  >{{ searchedPreviewData.length }} Records</b-badge
                >
              </div>
              <div class="d-flex align-items-center">
                 <b-form-input
                  v-model="searchQuery"
                  placeholder="Search Ledger Name..."
                  size="sm"
                  class="mr-2"
                  style="max-width: 200px;"
                ></b-form-input>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="togglePreview"
                >
                  <i
                    class="fa"
                    :class="showPreview ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                  {{ showPreview ? "Hide" : "Show" }} Preview
                </b-button>
              </div>
            </div>
          </template>

          <div v-show="showPreview">
            <div
              v-if="searchedPreviewData.length > 0"
              class="table-responsive"
              style="max-height: 400px; overflow: auto"
            >
              <table
                class="table table-striped table-bordered table-hover table-sm"
              >
                <thead
                  class="thead-light"
                  style="position: sticky; top: 0; z-index: 10"
                >
                  <tr>
                    <th>SN</th>
                    <th
                      v-for="column in displayColumns"
                      :key="column"
                      style="min-width: 120px"
                    >
                      {{ column }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in paginatedPreviewData" :key="index">
                    <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                    <td v-for="column in displayColumns" :key="column">
                      {{ row[column] || "-" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="alert alert-warning mb-0">
              <i class="fa fa-exclamation-triangle mr-2"></i>
              No valid data found. Please ensure your Excel file contains required columns.
            </div>
            <!-- Per Page Selector -->
            <div
              class="d-flex justify-content-between align-items-center mt-3 mb-2"
            >
              <div class="d-flex align-items-center">
                <label class="mr-2 mb-0">Rows per page:</label>
                <b-form-select
                  v-model="perPage"
                  :options="pageOptions"
                  size="sm"
                  @change="currentPage = 1"
                  class="w-auto"
                ></b-form-select>
              </div>
              <div>
                <small class="text-muted">
                  Showing {{ (currentPage - 1) * perPage + 1 }} to
                  {{ Math.min(currentPage * perPage, totalPreviewRows) }} of
                  {{ totalPreviewRows }} records
                </small>
              </div>
            </div>

            <!-- Pagination -->
            <b-pagination
              v-if="totalPreviewRows > perPage"
              v-model="currentPage"
              :total-rows="totalPreviewRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="mt-3"
            ></b-pagination>
          </div>
        </b-card>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-4">
        <b-row>
          <b-col md="12" class="text-right">
            <b-button
              variant="outline-secondary"
              @click="clearAll"
              :disabled="isLoading || (!selectedFile && jsonData.length === 0)"
              class="mr-2"
            >
              <i class="fa fa-times mr-1"></i>
              Clear All
            </b-button>
            <b-button
              variant="primary"
              @click="submit"
              :disabled="jsonData.length === 0 || isLoading"
              class="px-4"
            >
              <i class="fa fa-upload mr-1"></i>
              {{ isLoading ? "Uploading..." : "Submit Data" }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import LedgerOutstandingDashboardService from "../../services/LedgerOutstandingDashboardService.js";

export default {
  name: "LedgerOutstandingBulkUpload",
  data() {
    return {
      selectedFile: null,
      jsonData: [],
      isLoading: false,
      loadingMessage: "Processing file...",
      successMessage: "",
      errorMessage: "",
      showPreview: true,
      fileState: null,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      searchQuery: "",
    };
  },
  computed: {
    filteredPreviewData() {
      if (!this.jsonData || this.jsonData.length === 0) {
        return [];
      }

      return this.jsonData
        .map((row) => {
          const filteredRow = {};
            
          const keyMap = {
            "lom_doc_type": ["lom_doc_type"],
            "lom_doc_date": ["lom_doc_date"],
            "lom_doc_status": ["lom_doc_status"],
            "lom_ledger_name": ["lom_ledger_name", "Ledger Name"],
            "lom_gst_no": ["lom_gst_no"],
            "lom_pan_no": ["lom_pan_no"],
            "lom_comp_name": ["lom_comp_name"],
            "lom_ref_type": ["lom_ref_type"],
            "lom_date": ["lom_date"],
            "lom_inv_no": ["lom_inv_no"],
            "lom_reference_no": ["lom_reference_no"],
            "lom_pay_amt": ["lom_pay_amt"],
            "lom_pending_amt": ["lom_pending_amt"],
            "fk_client_id": ["clientId", "client_id"]
          };
            
          for (const [targetKey, sourceKeys] of Object.entries(keyMap)) {
               const foundKey = this.findKey(row, sourceKeys);
               if (foundKey) {
                   filteredRow[targetKey] = row[foundKey];
               }
          }

          return filteredRow;
        })
        .filter((row) => {
          if (Object.keys(row).length === 0) return false;
          // Simple validation: must have a ledger name or inv no
          return row["lom_ledger_name"] || row["lom_inv_no"];
        });
    },
    searchedPreviewData() {
      if (!this.searchQuery) {
        return this.filteredPreviewData;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.filteredPreviewData.filter(row => 
        row["lom_ledger_name"] && String(row["lom_ledger_name"]).toLowerCase().includes(query)
      );
    },
    displayColumns() {
      return [
        "lom_doc_type",
        "lom_doc_date",
        "lom_ledger_name",
        "lom_inv_no",
        "lom_pay_amt",
        "lom_pending_amt",
        "lom_reference_no"
      ];
    },
    paginatedPreviewData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.searchedPreviewData.slice(start, end);
    },
    totalPreviewRows() {
      return this.searchedPreviewData.length;
    },
  },
  methods: {
    handleFile(file) {
      let actualFile = file;
      if (!actualFile) {
        this.clearFile();
        return;
      }

      this.fileState = true;
      this.errorMessage = "";
      this.successMessage = "";
      this.isLoading = true;
      this.loadingMessage = "Reading Excel file...";

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rawData = XLSX.utils.sheet_to_json(sheet, { defval: null, raw: false });
          
          if (!rawData || rawData.length === 0) {
            this.errorMessage = "The selected Excel file appears to be empty.";
            this.fileState = false;
            return;
          }

          this.jsonData = rawData;
          this.successMessage = `Successfully loaded ${this.jsonData.length} records from Excel.`;
          this.showPreview = true;
          this.fileState = true;
        } catch (error) {
          console.error("Excel parse error:", error);
          this.errorMessage = "Failed to parse Excel file. Please ensure it's a valid .xlsx or .xls file.";
          this.fileState = false;
        } finally {
          this.isLoading = false;
        }
      };
      reader.onerror = () => {
        this.errorMessage = "Failed to read the file. Please try again.";
        this.isLoading = false;
        this.fileState = false;
      };
      reader.readAsBinaryString(actualFile);
    },
    async submit() {
      if (this.filteredPreviewData.length === 0) {
        this.errorMessage = "No valid records to upload. Please check your data.";
        return;
      }

      this.isLoading = true;
      this.loadingMessage = "Uploading data to server...";
      this.errorMessage = "";
      this.successMessage = "";
      
      try {
        const payload = { data: this.filteredPreviewData };
        const response = await LedgerOutstandingDashboardService.importLedgerOutstanding(payload);
        
        if (response.data && response.data.success) {
          this.successMessage = response.data.message || "Data imported successfully!";
          this.$emit("upload-success");
          // Clear after a short delay
          setTimeout(() => this.clearAll(), 3000);
        } else {
          this.errorMessage = response.data.message || "Server declined the import. Please check your data format.";
        }
      } catch (error) {
        console.error("Upload error:", error);
        const errorMsg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "An unexpected error occurred during upload. Please try again later.";
        this.errorMessage = errorMsg;
      } finally {
        this.isLoading = false;
      }
    },
    clearFile() {
      this.selectedFile = null;
      this.jsonData = [];
      this.fileState = null;
    },
    clearAll() {
      this.clearFile();
      this.showPreview = false;
    },
    togglePreview() {
      this.showPreview = !this.showPreview;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024, sizes = ["Bytes", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    findKey(obj, possibleKeys) {
      for (const key of possibleKeys) {
        if (obj.hasOwnProperty(key)) return key;
        const lowerKey = key.toLowerCase().trim();
        for (const objKey in obj) {
          if (objKey.toLowerCase().trim() === lowerKey) return objKey;
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.ledger-outstanding-upload-container { padding: 10px; }
.upload-card { position: relative; }
.loading-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex; align-items: center; justify-content: center; z-index: 10;
}
.table-responsive { max-height: 400px; }
.action-buttons { border-top: 1px solid #eee; padding-top: 15px; margin-top: 15px; }
</style>



// WEBPACK FOOTER //
// LedgerOutstandingBulkUpload.vue?583612c6